import * as R from 'ramda';
import { create } from 'zustand';
import {
  sendOfflineOrderToSync,
  validateSyncStatusOfOrders,
} from '../../../../services/modules/orders/orders.repository';
import useGeneralSettingsStore from '../../../../stores/GeneralSettings/useGeneralSettingsStore';
import { getOrderProductUniqueIdentifier } from '../../../../utils/getOrderProductUniqueIdentifier';
import { OrderProduct } from '../../../orders/products/orderProducts.types';
import { AvailabilityEnum } from '../../../products/ProductTypes';
import { toOfflineAvailability } from '../../../products/utils/availability.utils';
import { OfflineOrder } from '../../domain/offlineOrder/OfflineOrder';
import { offlineOrdersRepository } from '../../repositories/offlineOrders.repository';
import { offlineSkusRepository } from '../../repositories/offlineSkus.repository';
import { OfflineOrderTransformers } from '../../transformers/OfflineOrder.transformers';
import {
  CreateGridParams,
  UpdateOfflineOrderGeneralData,
} from './types/offlineOrders.actions.types';
import { OfflineOrdersStore } from './types/offlineOrders.types';
import { validateSellerCanCreateOrUpdateOrderWithoutCustomer } from './utils/offlineOrder.utils';
import { offlineOrderArtifactsGenerator } from './utils/offlineOrderArtifactsGenerator.util';
import { handleRemoveOfflineOrderFromOrderBook } from './utils/offlineOrderBook.utils';
import { offlineOrderChangeDataHandler } from './utils/offlineOrderChangeDataHandler.util';
import { getOfflineOrderGeneralDataChanges } from './utils/offlineOrderGeneralData.utils';

export const useOfflineOrdersStore = create<OfflineOrdersStore>()((_set, get) => ({
  create: async (customerId) => {
    const { customer, priceTable, salesRepresentative, seller } =
      await offlineOrderArtifactsGenerator.getArtifactsToCreateOrder(customerId);

    const { blockOrderWithoutCustomer } = useGeneralSettingsStore.getState().generalSettings;
    validateSellerCanCreateOrUpdateOrderWithoutCustomer({
      blockOrderWithoutCustomer,
      customerId,
    });

    const order = OfflineOrder.create({ customer, salesRepresentative, seller, priceTable });
    const savedOrder = await offlineOrdersRepository.save(order);

    return savedOrder;
  },
  updateObservation: async (id, observation) => {
    const order = await offlineOrdersRepository.getById(id);
    if (!order) {
      throw new Error('order not found');
    }

    order.setObservation(observation);

    const updatedOrder = await offlineOrdersRepository.update(order);

    return updatedOrder;
  },
  updateGeneralData: async (id, data: UpdateOfflineOrderGeneralData) => {
    const order = await offlineOrdersRepository.getById(id);
    if (!order) {
      throw new Error('order not found');
    }

    const changes = getOfflineOrderGeneralDataChanges({ order, data });

    if (changes.shouldChangePriceTable && data.priceTableId) {
      await offlineOrderChangeDataHandler.changePriceTable(order, data.priceTableId);
    }
    if (changes.shouldChangeSalesRepresentative) {
      await offlineOrderChangeDataHandler.changeSalesReps(order, data.salesRepresentativeId);
    }
    if (changes.shouldChangeSeller) {
      await offlineOrderChangeDataHandler.changeSeller(order, data.sellerId);
    }

    const updatedOrder = await offlineOrdersRepository.update(order);

    return updatedOrder;
  },
  updateOrderCustomer: async (id: string, customerId: string | null) => {
    const order = await offlineOrdersRepository.getById(id);
    if (!order) {
      throw new Error('order not found');
    }
    const { blockOrderWithoutCustomer } = useGeneralSettingsStore.getState().generalSettings;

    await offlineOrderChangeDataHandler.changeOrderCustomer({
      order,
      blockOrderWithoutCustomer,
      customerId,
    });

    const updatedOrder = await offlineOrdersRepository.update(order);

    return updatedOrder;
  },
  addAllPossibleProductsInOrder: async (id) => {
    const { addProductBasedOnGrid } = get();

    const order = await offlineOrdersRepository.getById(id);
    if (!order) {
      throw new Error('order not found');
    }

    const gridItems = await offlineOrderArtifactsGenerator.getArtifactsToAddAllProducts();

    for (const gridItem of gridItems) {
      try {
        await addProductBasedOnGrid({ orderId: id, gridItems: [gridItem] });
      } catch (_error) {
        console.warn('error adding product when adding all, expected');
      }
    }
  },
  addKitProductsBasedOnGrid: async ({ orderId, gridItems, kitId }) => {
    const gridItemsBasedOnProductColor = R.groupBy((item) => {
      if (!item.productColorId) {
        console.error('addKitProductsBasedOnGrid: item without productColorId', item);
        throw new Error('adding kit item without productColorId');
      }

      return getOrderProductUniqueIdentifier({
        productColorId: item.productColorId,
        availability: item.availability,
        deliveryWindow: item.deliveryWindowId
          ? ({ id: item.deliveryWindowId } as OrderProduct['deliveryWindow'])
          : undefined,
      });
    }, gridItems);

    for (const groupedGridItemsKey in gridItemsBasedOnProductColor) {
      const key = groupedGridItemsKey as keyof typeof gridItemsBasedOnProductColor;
      const gridItemsForThisProductColor = gridItemsBasedOnProductColor[key];

      const { addProductBasedOnGrid } = get();
      await addProductBasedOnGrid({
        orderId,
        gridItems: gridItemsForThisProductColor ?? [],
        kitId,
      });
    }

    const updatedOrder = await offlineOrdersRepository.getById(orderId);
    if (!updatedOrder) {
      throw new Error('order not found after adding kit products');
    }
    return updatedOrder;
  },
  addProductBasedOnGrid: async ({ orderId, gridItems, kitId }) => {
    const order = await offlineOrdersRepository.getById(orderId);
    if (!order || !gridItems.length) {
      throw new Error('order not found');
    }

    const { availability } = gridItems[0];

    const {
      kit,
      product,
      category,
      collection,
      classifications,
      artifactsToGridItem,
      deliveryWindow,
      productColor,
      productColorPriceInfo,
    } = await offlineOrderArtifactsGenerator.getArtifactsToAddProductBasedOnGrid({
      order,
      gridItems,
      kitId,
    });

    order.addProduct({
      kit,
      product,
      productColor,
      availability,
      deliveryWindow,
      productColorPriceInfo,
      productCategory: category,
      grid: artifactsToGridItem,
      productCollection: collection,
      productColorClassifications: classifications,
    });

    const updatedOrder = await offlineOrdersRepository.update(order);

    return updatedOrder;
  },
  addProductBasedOnPack: async (id, orderProductAndPacks) => {
    const order = await offlineOrdersRepository.getById(id);
    if (!order) {
      throw new Error('order not found');
    }

    const {
      category,
      collection,
      classifications,
      artifactsToGridItem,
      deliveryWindow,
      product,
      productColor,
      productColorPriceInfo,
      packsWithMultiplier,
    } = await offlineOrderArtifactsGenerator.getArtifactsToAddProductBasedOnPacks(
      order,
      orderProductAndPacks,
    );

    order.addProduct({
      product,
      productColor,
      deliveryWindow,
      packsWithMultiplier,
      productColorPriceInfo,
      grid: artifactsToGridItem,
      productCategory: category,
      productCollection: collection,
      availability: orderProductAndPacks.availability,
      productColorClassifications: classifications,
    });

    const updatedOrder = await offlineOrdersRepository.update(order);

    return updatedOrder;
  },
  changeProductGrid: async ({ orderId, orderProductIds, gridItemsIdsAndQuantity }) => {
    const order = await offlineOrdersRepository.getById(orderId);
    if (!order) {
      throw new Error('order not found');
    }

    order.changeProductGrid({
      orderProductIds,
      gridValues: gridItemsIdsAndQuantity,
    });

    const updatedOrder = await offlineOrdersRepository.update(order);

    return updatedOrder;
  },
  changeProductGridBasedOnPacks: async (params) => {
    const { orderId, orderProductIds } = params;

    const order = await offlineOrdersRepository.getById(orderId);
    if (!order) {
      throw new Error('order not found');
    }

    const { packsWithMultiplier, productColorWithAllPacks } =
      await offlineOrderArtifactsGenerator.getArtifactsToChangeProductGridBasedOnPacks(params);

    order.changeProductGridBasedOnPacks({
      orderProductIds,
      packsWithMultiplier,
      productColorWithAllPacks,
    });

    const updatedOrder = await offlineOrdersRepository.update(order);

    return updatedOrder;
  },
  changeOrderProductAvailability: async (
    id,
    { anyGridItemId, deliveryWindowId, newAvailability },
  ) => {
    const order = await offlineOrdersRepository.getById(id);
    if (!order) {
      throw new Error('order not found');
    }

    const oldOrderProduct = order.getOrderProductByGridItemId(anyGridItemId);
    if (!oldOrderProduct) {
      throw new Error('order product not found');
    }

    const offlineNewAvailability = toOfflineAvailability(newAvailability);
    const newGridItems = oldOrderProduct.grid.map<CreateGridParams>((gridItem) =>
      offlineNewAvailability !== AvailabilityEnum.READY_FOR_DELIVERY
        ? {
            quantity: 0,
            skuId: gridItem.sku.id,
            availability: offlineNewAvailability,
            deliveryWindowId: deliveryWindowId!,
            productColorId: oldOrderProduct.productColor.id,
          }
        : {
            quantity: 0,
            skuId: gridItem.sku.id,
            availability: offlineNewAvailability,
            productColorId: oldOrderProduct.productColor.id,
          },
    );

    const {
      category,
      collection,
      classifications,
      artifactsToGridItem,
      deliveryWindow,
      product,
      productColor,
      productColorPriceInfo,
    } = await offlineOrderArtifactsGenerator.getArtifactsToAddProductBasedOnGrid({
      order,
      gridItems: newGridItems,
    });

    order.createProductCopyGridAndReplace(oldOrderProduct, {
      product,
      productColor,
      deliveryWindow,
      productColorPriceInfo,
      grid: artifactsToGridItem,
      availability: offlineNewAvailability,
      packsWithMultiplier: oldOrderProduct.packs,
      productCategory: category,
      productCollection: collection,
      productColorClassifications: classifications,
    });

    const updatedOrder = await offlineOrdersRepository.update(order);

    return updatedOrder;
  },
  changeOrderProductColor: async (id, { anyGridItemId, newProductColorId }) => {
    const order = await offlineOrdersRepository.getById(id);
    if (!order) {
      throw new Error('order not found');
    }

    const oldOrderProduct = order.getOrderProductByGridItemId(anyGridItemId);
    if (!oldOrderProduct) {
      throw new Error('order product not found');
    }

    const skuToCreateGrid = await offlineSkusRepository.findOneByProductColorId(newProductColorId);
    if (!skuToCreateGrid) {
      throw new Error('sku not found to product color');
    }

    const offlineAvailability = toOfflineAvailability(oldOrderProduct.availability);
    const newGridItem: CreateGridParams =
      offlineAvailability !== AvailabilityEnum.READY_FOR_DELIVERY
        ? {
            quantity: 0,
            skuId: skuToCreateGrid.id,
            productColorId: newProductColorId,
            availability: offlineAvailability,
            deliveryWindowId: oldOrderProduct.deliveryWindow!.id,
          }
        : {
            quantity: 0,
            skuId: skuToCreateGrid.id,
            productColorId: newProductColorId,
            availability: offlineAvailability,
          };

    const {
      artifactsToGridItem,
      deliveryWindow,
      product,
      productColor,
      productColorPriceInfo,
      classifications,
      category,
      collection,
    } = await offlineOrderArtifactsGenerator.getArtifactsToAddProductBasedOnGrid({
      order,
      gridItems: [newGridItem],
    });

    order.createProductCopyGridAndReplace(oldOrderProduct, {
      product,
      productColor,
      deliveryWindow,
      productColorPriceInfo,
      productCategory: category,
      grid: artifactsToGridItem,
      productCollection: collection,
      availability: offlineAvailability,
      packsWithMultiplier: oldOrderProduct.packs,
      productColorClassifications: classifications,
    });

    const updatedOrder = await offlineOrdersRepository.update(order);

    return updatedOrder;
  },
  removeKitOrderProducts: async ({ orderId, kitId }) => {
    const order = await offlineOrdersRepository.getById(orderId);
    if (!order) {
      throw new Error('order not found');
    }

    const productsToRemove = order.products?.filter((p) => p.kit?.id === kitId);
    if (!productsToRemove?.length) {
      throw new Error('order products not found');
    }

    order.removeOrderProducts(productsToRemove);
    const updatedOrder = await offlineOrdersRepository.update(order);

    return updatedOrder;
  },
  removeOrderProductsFromOrder: async (orderId, getProductsFn) => {
    const order = await offlineOrdersRepository.getById(orderId);
    if (!order) {
      throw new Error('order not found');
    }

    const productsToRemove = getProductsFn(order);

    if (!productsToRemove?.length) {
      throw new Error('order products not found');
    }

    order.removeOrderProducts(productsToRemove);

    const updatedOrder = offlineOrdersRepository.update(order);

    return updatedOrder;
  },
  removeOrderProduct: async (orderId, orderProductIds) => {
    const { removeOrderProductsFromOrder } = get();

    const updatedOrder = await removeOrderProductsFromOrder(orderId, (order) => {
      const orderProductToRemove = order.getOrderProduct(orderProductIds);
      return orderProductToRemove ? [orderProductToRemove] : [];
    });

    return updatedOrder;
  },
  removeAllOrderProductsFromProductColor: async (orderId, productColorId) => {
    const { removeOrderProductsFromOrder } = get();

    const updatedOrder = await removeOrderProductsFromOrder(orderId, (order) =>
      order.getOrderProductsFromProductColorId(productColorId),
    );

    return updatedOrder;
  },
  removeAllOrderProductsFromProductColors: async (orderId, productColorIds) => {
    const { removeOrderProductsFromOrder } = get();

    const updatedOrder = await removeOrderProductsFromOrder(orderId, (order) =>
      order.getOrderProductsFromProductColorIds(productColorIds),
    );

    return updatedOrder;
  },
  syncOfflineOrder: async (id) => {
    const order = await offlineOrdersRepository.getById(id);
    if (!order) {
      throw new Error('order not found');
    }

    const apiOfflineOrder = OfflineOrderTransformers.toSendOfflineOrderToSyncInput(order);
    const syncRes = await sendOfflineOrderToSync(apiOfflineOrder);

    if (syncRes.ok && syncRes.data) {
      handleRemoveOfflineOrderFromOrderBook(order.id);

      order.startSyncing({
        onlineOrderId: syncRes.data.id,
        onlineOrderCode: syncRes.data.code,
      });
    } else {
      const errorCode = !syncRes.ok ? syncRes.data?.code : undefined;

      order.errorSyncing(errorCode);
    }

    const updatedOrder = await offlineOrdersRepository.update(order);

    return updatedOrder;
  },
  validateSyncingOrdersAndReturnResult: async (ids) => {
    const offlineOrders = ids?.length
      ? await offlineOrdersRepository.findSyncingOrdersByIds(ids)
      : await offlineOrdersRepository.findSyncingOrders();

    if (!offlineOrders.length) {
      return {
        syncedOfflineOrders: [],
        unsyncedOfflineOrders: [],
        partiallySyncedOfflineOrders: [],
      };
    }

    const onlineOrderIds = offlineOrders.map(
      (order) => order.metadata?.onlineMetadata?.onlineOrderId!,
    );

    const validateSyncStatusRes = await validateSyncStatusOfOrders({ ids: onlineOrderIds });

    if (!validateSyncStatusRes.ok || !validateSyncStatusRes.data) {
      console.warn(
        'validation of sync status failed, returning empty results.',
        validateSyncStatusRes,
      );
      return {
        syncedOfflineOrders: [],
        unsyncedOfflineOrders: [],
        partiallySyncedOfflineOrders: [],
      };
    }

    const validateSyncStatusData = validateSyncStatusRes.data;

    const onlineOrderIdsWithErrors = validateSyncStatusData.orderIdsWithSyncError;
    const onlineOrderIdsWithSuccess = [
      ...validateSyncStatusData.orderIdsWithSyncSuccess,
      ...validateSyncStatusData.orderIdsPartiallySynced,
    ];

    const offlineOrdersWithErrorSyncing = offlineOrders.filter(
      (order) =>
        order.metadata?.onlineMetadata?.onlineOrderId &&
        onlineOrderIdsWithErrors.includes(order.metadata.onlineMetadata.onlineOrderId),
    );

    const offlineOrdersWithSuccessSyncing = offlineOrders.filter(
      (order) =>
        order.metadata?.onlineMetadata?.onlineOrderId &&
        onlineOrderIdsWithSuccess.includes(order.metadata.onlineMetadata.onlineOrderId),
    );

    const offlineOrdersWithPartiallySyncing = offlineOrders.filter(
      (order) =>
        order.metadata?.onlineMetadata?.onlineOrderId &&
        validateSyncStatusData.orderIdsPartiallySynced.includes(
          order.metadata.onlineMetadata.onlineOrderId,
        ),
    );

    if (offlineOrdersWithErrorSyncing.length) {
      offlineOrdersWithErrorSyncing.forEach((order) => order.errorSyncing());
      await offlineOrdersRepository.updateMany(offlineOrdersWithErrorSyncing);
    }

    if (offlineOrdersWithSuccessSyncing.length) {
      await offlineOrdersRepository.deleteMany(
        offlineOrdersWithSuccessSyncing.map((order) => order.code),
      );
    }

    return {
      syncedOfflineOrders: offlineOrdersWithSuccessSyncing,
      unsyncedOfflineOrders: offlineOrdersWithErrorSyncing,
      partiallySyncedOfflineOrders: offlineOrdersWithPartiallySyncing,
    };
  },
}));
